import { useCallback, useEffect, useRef } from 'react';


export interface UseAbortControllerResult {
  createSignal: () => AbortSignal;
  abort: () => void;
}

/**
 * Manages an AbortController across renders.
 *
 * - `createSignal()` aborts any pending request and returns a fresh signal.
 *   Call it at the start of each fetch so the previous one is cancelled.
 * - `abort()` cancels the current signal immediately (e.g. on form close).
 * - On component unmount the active signal is aborted automatically.
 *
 * @example — list hook (cancels stale fetches on deps change)
 * ```ts
 * const { createSignal } = useAbortController();
 *
 * const fetchData = useCallback(async () => {
 *   const signal = createSignal();
 *   const response = await service.list(params, signal);
 *   if (response.error?.code === 'ABORTED') return;
 *   setRows(response.data);
 * }, [createSignal, ...deps]);
 * ```
 *
 * @example — profile hook (cancels on unmount)
 * ```ts
 * useEffect(() => {
 *   const controller = new AbortController();
 *   void loadProfile(controller.signal);
 *   return () => controller.abort();
 * }, [id]);
 * ```
 */
export function useAbortController(): UseAbortControllerResult {
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => {
    controllerRef.current?.abort();
  }, []);

  const createSignal = useCallback((): AbortSignal => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    return controller.signal;
  }, []);

  const abort = useCallback((): void => {
    controllerRef.current?.abort();
  }, []);

  return { createSignal, abort };
}
